"use client";

import {
  ACQUISITION_DEMO_EVENTS,
  type AcquisitionDemoAnalyticsContext,
  type AcquisitionDemoEventName,
  type AcquisitionDemoEventProperties,
  type AcquisitionDemoVideoProgress,
  trackAcquisitionDemoEvent,
} from "@/lib/analytics/acquisition-demo-events";
import { Play } from "lucide-react";
import { usePostHog } from "posthog-js/react";
import { useCallback, useEffect, useRef, useState } from "react";

type DemoVideoProps = {
  src: string;
  poster: string;
  appearance?: "default" | "brand";
  analyticsContext?: AcquisitionDemoAnalyticsContext;
  onAnalyticsEvent?: (
    eventName: AcquisitionDemoEventName,
    properties: AcquisitionDemoEventProperties
  ) => void;
};

const VIDEO_PROGRESS_MILESTONES: AcquisitionDemoVideoProgress[] = [25, 50, 75];

export function DemoVideo({
  src,
  poster,
  appearance = "default",
  analyticsContext,
  onAnalyticsEvent,
}: DemoVideoProps) {
  const posthog = usePostHog();
  const videoRef = useRef<HTMLVideoElement>(null);
  const hasCompletedRef = useRef(false);
  const hasStartedRef = useRef(false);
  const hasViewedRef = useRef(false);
  const observedProgressRef = useRef(new Set<AcquisitionDemoVideoProgress>());
  const viewTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [hasStarted, setHasStarted] = useState(false);

  const emitAnalyticsEvent = useCallback(
    (
      eventName: AcquisitionDemoEventName,
      properties: Pick<AcquisitionDemoEventProperties, "video_progress_percent"> = {}
    ) => {
      if (!analyticsContext) return;

      const eventProperties = trackAcquisitionDemoEvent(
        posthog,
        eventName,
        analyticsContext,
        properties
      );
      onAnalyticsEvent?.(eventName, eventProperties);
    },
    [analyticsContext, onAnalyticsEvent, posthog]
  );

  useEffect(() => {
    const video = videoRef.current;
    if (!analyticsContext || !video || typeof IntersectionObserver === "undefined") return;

    const clearViewTimer = () => {
      if (viewTimerRef.current) {
        clearTimeout(viewTimerRef.current);
        viewTimerRef.current = null;
      }
    };

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry || hasViewedRef.current) return;

        if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
          if (!viewTimerRef.current) {
            viewTimerRef.current = setTimeout(() => {
              hasViewedRef.current = true;
              viewTimerRef.current = null;
              emitAnalyticsEvent(ACQUISITION_DEMO_EVENTS.viewed);
              observer.disconnect();
            }, 1000);
          }
          return;
        }

        clearViewTimer();
      },
      { threshold: 0.5 }
    );

    observer.observe(video);

    return () => {
      clearViewTimer();
      observer.disconnect();
    };
  }, [analyticsContext, emitAnalyticsEvent]);

  const handlePlayClick = () => {
    setHasStarted(true);
    void videoRef.current?.play();
  };

  const handlePlay = () => {
    setHasStarted(true);
    if (hasStartedRef.current) return;

    hasStartedRef.current = true;
    emitAnalyticsEvent(ACQUISITION_DEMO_EVENTS.started);
  };

  const handleTimeUpdate = () => {
    const video = videoRef.current;
    if (!video || !Number.isFinite(video.duration) || video.duration <= 0) return;

    const progressPercent = (video.currentTime / video.duration) * 100;
    VIDEO_PROGRESS_MILESTONES.forEach((milestone) => {
      if (progressPercent < milestone || observedProgressRef.current.has(milestone)) return;

      observedProgressRef.current.add(milestone);
      emitAnalyticsEvent(ACQUISITION_DEMO_EVENTS.progressed, {
        video_progress_percent: milestone,
      });
    });
  };

  const handleEnded = () => {
    if (hasCompletedRef.current) return;

    hasCompletedRef.current = true;
    emitAnalyticsEvent(ACQUISITION_DEMO_EVENTS.completed);
  };

  return (
    <div className="relative mx-auto aspect-video w-full max-w-4xl overflow-hidden rounded-xl bg-black">
      <video
        ref={videoRef}
        src={src}
        poster={poster}
        controls
        playsInline
        preload="metadata"
        onEnded={handleEnded}
        onPlay={handlePlay}
        onTimeUpdate={handleTimeUpdate}
        className="h-full w-full bg-black object-cover"
        aria-label="ChickyTutor video demo showing how the AI language tutor works"
      />
      {!hasStarted && (
        <button
          type="button"
          onClick={handlePlayClick}
          className="absolute inset-0 flex items-center justify-center bg-transparent"
          aria-label="Play ChickyTutor video demo"
        >
          <span
            className={`flex h-16 w-24 items-center justify-center rounded-[18px] shadow-2xl transition-colors ${
              appearance === "brand"
                ? "bg-[#FD954F] text-[#1C1C1E] hover:bg-[#F28747]"
                : "bg-red-600/95 text-white hover:bg-red-600"
            }`}
          >
            <Play className="ml-1 h-9 w-9 fill-current" strokeWidth={0} aria-hidden />
          </span>
        </button>
      )}
    </div>
  );
}
